import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Megaphone, Send, Search, Eye, Check, Clock } from "lucide-react";

const TIPO_LBL = { anuncio: "Anuncio", notificacion_dirigida: "Dirigida", aviso_urgente: "Urgente" };
const TIPO_COLOR = { anuncio: "bg-primary text-primary-foreground", notificacion_dirigida: "bg-accent text-accent-foreground", aviso_urgente: "bg-destructive text-destructive-foreground" };

export default function Comunicaciones() {
    const [items, setItems] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [stats, setStats] = useState({});
    const [open, setOpen] = useState(false);
    const [lectoresFor, setLectoresFor] = useState(null);
    const [form, setForm] = useState({
        titulo: "", mensaje: "", tipo: "anuncio", destinatarios: "todo_edificio",
        unidades_destino: [], usuarios_destino: [], publicado: true, fecha_expiracion: "",
    });

    const load = async () => {
        const r = await api.get("/comunicaciones/all");
        setItems(r.data);
        const ss = {};
        for (const c of r.data) {
            try { ss[c.comunicacion_id] = (await api.get(`/comunicaciones/${c.comunicacion_id}/stats`)).data; } catch { }
        }
        setStats(ss);
    };
    useEffect(() => {
        api.get("/unidades").then((r) => setUnidades(r.data));
        api.get("/usuarios-edificio").then((r) => setUsuarios(r.data));
        load();
    }, []);

    const submit = async () => {
        try {
            const payload = { ...form };
            if (payload.fecha_expiracion) payload.fecha_expiracion = new Date(payload.fecha_expiracion).toISOString();
            else delete payload.fecha_expiracion;
            await api.post("/comunicaciones", payload);
            toast.success("Publicada");
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (c) => {
        if (!window.confirm("¿Eliminar esta comunicación?")) return;
        try { await api.delete(`/comunicaciones/${c.comunicacion_id}`); toast.success("Eliminada"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleUnidad = (id) => {
        setForm((f) => ({ ...f, unidades_destino: f.unidades_destino.includes(id) ? f.unidades_destino.filter((x) => x !== id) : [...f.unidades_destino, id] }));
    };

    const [uniSearch, setUniSearch] = useState("");
    const unidadesFiltradas = useMemo(() => {
        const q = uniSearch.trim().toLowerCase();
        const sorted = [...unidades].sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es", { numeric: true }));
        if (!q) return sorted;
        return sorted.filter((u) => u.etiqueta.toLowerCase().includes(q));
    }, [unidades, uniSearch]);
    const seleccionarTodas = () => {
        setForm((f) => ({ ...f, unidades_destino: unidadesFiltradas.map((u) => u.unidad_id) }));
    };
    const limpiarSeleccion = () => {
        setForm((f) => ({ ...f, unidades_destino: [] }));
    };

    return (
        <div>
            <PageHeader title="Comunicaciones" subtitle="Anuncios, notificaciones dirigidas y avisos urgentes."
                actions={<Button onClick={() => { setForm({ titulo: "", mensaje: "", tipo: "anuncio", destinatarios: "todo_edificio", unidades_destino: [], usuarios_destino: [], publicado: true, fecha_expiracion: "" }); setOpen(true); }} data-testid="com-new"><Plus className="h-4 w-4 mr-1" /> Nueva</Button>} />

            <div className="grid gap-2">
                {items.map((c) => (
                    <Card key={c.comunicacion_id}>
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3">
                                    <div className={`h-9 w-9 rounded-md grid place-items-center ${c.tipo === "aviso_urgente" ? "bg-destructive-soft text-destructive" : "bg-primary-soft text-primary"}`}>
                                        {c.tipo === "aviso_urgente" ? <AlertTriangle className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
                                    </div>
                                    <div>
                                        <p className="font-heading font-semibold">{c.titulo}</p>
                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-xl">{c.mensaje}</p>
                                        <div className="flex items-center gap-2 mt-2 text-[11px]">
                                            <Badge className={TIPO_COLOR[c.tipo] || ""}>{TIPO_LBL[c.tipo]}</Badge>
                                            <span className="text-muted-foreground">→ {c.destinatarios.replace("_", " ")}</span>
                                            {stats[c.comunicacion_id] && (
                                                <>
                                                    <span className="font-mono text-muted-foreground">· leídas {stats[c.comunicacion_id].leidas}/{stats[c.comunicacion_id].total}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setLectoresFor(c)}
                                                        className="inline-flex items-center gap-1 text-accent hover:text-primary hover:underline"
                                                        data-testid={`com-ver-lectores-${c.comunicacion_id}`}
                                                    >
                                                        <Eye className="h-3 w-3" /> Ver lectores
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
                        <DialogTitle>Nueva comunicación</DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                        <div className="space-y-2"><Label>Título</Label>
                            <Input data-testid="com-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
                        <div className="space-y-2"><Label>Mensaje</Label>
                            <Textarea rows={4} data-testid="com-mensaje" value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Tipo</Label>
                                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                    <SelectTrigger data-testid="com-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="anuncio">Anuncio</SelectItem>
                                        <SelectItem value="notificacion_dirigida">Notificación dirigida</SelectItem>
                                        <SelectItem value="aviso_urgente">Aviso urgente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2"><Label>Destinatarios</Label>
                                <Select value={form.destinatarios} onValueChange={(v) => setForm({ ...form, destinatarios: v, unidades_destino: [], usuarios_destino: [] })}>
                                    <SelectTrigger data-testid="com-dest"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="todo_edificio">Todo el edificio</SelectItem>
                                        <SelectItem value="unidades_especificas">Unidades específicas</SelectItem>
                                        <SelectItem value="un_usuario">Un usuario</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {form.destinatarios === "unidades_especificas" && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <Label>Unidades ({form.unidades_destino.length})</Label>
                                    <div className="flex gap-1">
                                        <button type="button" onClick={seleccionarTodas} className="text-[11px] text-accent hover:text-primary hover:underline">Seleccionar todas</button>
                                        {form.unidades_destino.length > 0 && (
                                            <>
                                                <span className="text-[11px] text-muted-foreground">·</span>
                                                <button type="button" onClick={limpiarSeleccion} className="text-[11px] text-destructive hover:underline">Limpiar</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                        value={uniSearch}
                                        onChange={(e) => setUniSearch(e.target.value)}
                                        placeholder="Buscar unidad…"
                                        className="pl-7 h-8 text-sm"
                                        data-testid="com-uni-search"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-1 max-h-[180px] overflow-y-auto border border-border rounded-md p-2 bg-muted/20">
                                    {unidadesFiltradas.length === 0 ? (
                                        <p className="text-xs text-muted-foreground py-1 px-1">Sin coincidencias.</p>
                                    ) : unidadesFiltradas.map((u) => {
                                        const has = form.unidades_destino.includes(u.unidad_id);
                                        return (
                                            <button
                                                key={u.unidad_id}
                                                type="button"
                                                onClick={() => toggleUnidad(u.unidad_id)}
                                                data-testid={`com-uni-${u.etiqueta}`}
                                                className={`text-xs px-2 py-1 rounded-md border whitespace-nowrap ${has ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-primary-soft"}`}
                                            >
                                                {u.etiqueta}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {form.destinatarios === "un_usuario" && (
                            <div className="space-y-2"><Label>Usuario</Label>
                                <Select value={form.usuarios_destino[0] || ""} onValueChange={(v) => setForm({ ...form, usuarios_destino: [v] })}>
                                    <SelectTrigger><SelectValue placeholder="Elegir" /></SelectTrigger>
                                    <SelectContent>
                                        {usuarios.map((u) => <SelectItem key={u.usuario_edif_id} value={u.usuario_edif_id}>{u.nombre} ({u.email})</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-2"><Label>Expira (opcional)</Label>
                            <Input type="datetime-local" value={form.fecha_expiracion} onChange={(e) => setForm({ ...form, fecha_expiracion: e.target.value })} /></div>
                    </div>

                    <DialogFooter className="px-6 py-3 border-t border-border shrink-0 bg-background">
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} data-testid="com-submit"><Send className="h-4 w-4 mr-1" />Publicar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <LectoresModal
                comunicacion={lectoresFor}
                onClose={() => setLectoresFor(null)}
            />
        </div>
    );
}

function fmtFechaCorta(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const ayer = new Date(today); ayer.setDate(ayer.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay(d, today)) return `Hoy ${hh}:${mm}`;
    if (sameDay(d, ayer)) return `Ayer ${hh}:${mm}`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mo} ${hh}:${mm}`;
}

function LectoresModal({ comunicacion, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState("todos");
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!comunicacion) { setData(null); return; }
        setLoading(true);
        setFiltro("todos");
        setQ("");
        api.get(`/comunicaciones/${comunicacion.comunicacion_id}/lectores`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [comunicacion]);

    const filtered = useMemo(() => {
        if (!data) return [];
        const ql = q.trim().toLowerCase();
        return data.items.filter((u) => {
            if (filtro === "leidas" && !u.leida) return false;
            if (filtro === "no_leidas" && u.leida) return false;
            if (!ql) return true;
            return (u.nombre || "").toLowerCase().includes(ql)
                || (u.email || "").toLowerCase().includes(ql)
                || (u.unidad_etiqueta || "").toLowerCase().includes(ql);
        });
    }, [data, filtro, q]);

    return (
        <Dialog open={!!comunicacion} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col">
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
                    <DialogTitle className="truncate">{comunicacion?.titulo || "Lectores"}</DialogTitle>
                    {data && (
                        <div className="flex gap-4 mt-2 text-xs">
                            <span><span className="text-muted-foreground">Total:</span> <span className="font-semibold">{data.total}</span></span>
                            <span><span className="text-muted-foreground">Leídas:</span> <span className="font-semibold text-success">{data.leidas}</span></span>
                            <span><span className="text-muted-foreground">No leídas:</span> <span className="font-semibold text-warning">{data.no_leidas}</span></span>
                        </div>
                    )}
                </DialogHeader>

                <div className="px-6 py-3 border-b border-border bg-muted/30 shrink-0 space-y-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por nombre, email o unidad…"
                            className="pl-8 h-9"
                            data-testid="lectores-search"
                        />
                    </div>
                    <div className="inline-flex rounded-md border border-border overflow-hidden h-8 text-xs">
                        {[
                            { v: "todos", l: `Todos${data ? ` (${data.total})` : ""}` },
                            { v: "leidas", l: `Leídas${data ? ` (${data.leidas})` : ""}` },
                            { v: "no_leidas", l: `No leídas${data ? ` (${data.no_leidas})` : ""}` },
                        ].map((t) => (
                            <button
                                key={t.v}
                                type="button"
                                onClick={() => setFiltro(t.v)}
                                data-testid={`lectores-filter-${t.v}`}
                                className={`px-3 font-medium transition-colors ${filtro === t.v ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                            >
                                {t.l}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-3">
                    {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}
                    {!loading && filtered.length === 0 && (
                        <p className="text-sm text-muted-foreground py-6 text-center">Sin destinatarios para este filtro.</p>
                    )}
                    {!loading && filtered.length > 0 && (
                        <div className="divide-y divide-border">
                            {filtered.map((u) => (
                                <div key={u.usuario_edif_id} className="py-2 flex items-center gap-3" data-testid={`lectores-row-${u.usuario_edif_id}`}>
                                    <div className={`h-7 w-7 rounded-full grid place-items-center shrink-0 ${u.leida ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>
                                        {u.leida ? <Check className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">
                                            {u.nombre || "(Sin nombre)"}
                                            {u.es_master && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">Resp.</span>}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            <span className="font-mono">{u.unidad_etiqueta}</span>
                                            {u.email && <span> · {u.email}</span>}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {u.leida ? (
                                            <span className="text-[11px] text-muted-foreground font-mono">{fmtFechaCorta(u.fecha_lectura)}</span>
                                        ) : (
                                            <span className="text-[11px] text-warning">Pendiente</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter className="px-6 py-3 border-t border-border shrink-0 bg-background">
                    <Button onClick={onClose}>Cerrar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
